import React, { useMemo } from 'react'
import { Button, Space, Typography } from 'antd'
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  VerticalAlignBottomOutlined,
  VerticalAlignTopOutlined
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { Dimension } from '../../../../../features/dimension/types/Dimension'
import { isOwnSource } from '../utils/dimensionAccess'
import { buildBlockReorderBatch, buildMoveToEdgeBatch } from '../utils/dimensionMutationBatch'

const { Text } = Typography

interface DimensionActionBarProps {
  flatList: Dimension[]
  selectedIds: string[]
  onMoveBlock: (direction: 'up' | 'down') => void
  onMoveToEdge: (edge: 'start' | 'end') => void
  onMoveUnder: () => void
  onDelete: () => void
  onClear: () => void
}

const DimensionActionBar: React.FC<DimensionActionBarProps> = ({
  flatList,
  selectedIds,
  onMoveBlock,
  onMoveToEdge,
  onMoveUnder,
  onDelete,
  onClear
}) => {
  const { t } = useTranslation()

  const breakdown = useMemo(() => {
    const byId = new Map(flatList.map(d => [d.dimensionId, d]))
    let own = 0
    let integration = 0
    selectedIds.forEach(id => {
      const dimension = byId.get(id)
      if (!dimension) return
      if (isOwnSource(dimension.source)) own += 1
      else integration += 1
    })
    return { own, integration }
  }, [flatList, selectedIds])

  const canMoveUp = useMemo(
    () => buildBlockReorderBatch(flatList, selectedIds, 'up').length > 0,
    [flatList, selectedIds]
  )
  const canMoveDown = useMemo(
    () => buildBlockReorderBatch(flatList, selectedIds, 'down').length > 0,
    [flatList, selectedIds]
  )
  const canMoveToStart = useMemo(
    () => buildMoveToEdgeBatch(flatList, selectedIds, 'start').length > 0,
    [flatList, selectedIds]
  )
  const canMoveToEnd = useMemo(
    () => buildMoveToEdgeBatch(flatList, selectedIds, 'end').length > 0,
    [flatList, selectedIds]
  )

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 16px',
        marginBottom: 16,
        background: '#e6f4ff',
        border: '1px solid #91caff',
        borderRadius: 8
      }}
    >
      <Space size={12}>
        <Text strong>{t('dimensionsPage:selectionCount', { count: selectedIds.length })}</Text>
        <Text type="secondary">{t('dimensionsPage:selectionBreakdown', breakdown)}</Text>
      </Space>
      <Space wrap>
        <Button icon={<VerticalAlignTopOutlined />} disabled={!canMoveToStart} onClick={() => onMoveToEdge('start')} />
        <Button icon={<ArrowUpOutlined />} disabled={!canMoveUp} onClick={() => onMoveBlock('up')} />
        <Button icon={<ArrowDownOutlined />} disabled={!canMoveDown} onClick={() => onMoveBlock('down')} />
        <Button icon={<VerticalAlignBottomOutlined />} disabled={!canMoveToEnd} onClick={() => onMoveToEdge('end')} />
        <Button onClick={onMoveUnder}>{t('dimensionsPage:actionMoveUnder')}</Button>
        <Button danger onClick={onDelete}>
          {t('dimensionsPage:actionDeleteN', { count: selectedIds.length })}
        </Button>
        <Button type="text" onClick={onClear}>
          {t('dimensionsPage:selectionClear')}
        </Button>
      </Space>
    </div>
  )
}

export default DimensionActionBar
